// All balance tunables for Marshmallow Madness live here. Pure data — no Phaser,
// no randomness. Units are documented per-constant; all *_PER_SEC rates are
// applied via dt-based integration so feel is frame-rate independent.
//
// Heat is a 0..100 scalar. The player controls `distanceNorm` (0 = at the
// flame, 1 = far away). Net heat = flameIntensity*(1-distance) - cooling, so
// there is an equilibrium distance where heat holds steady inside the band.

/** Heat gauge bounds. */
export const HEAT_MIN = 0;
export const HEAT_MAX = 100;

/** Golden band [min, max] at base difficulty — heat in this range toasts. */
export const GOLDEN_BAND: readonly [number, number] = [55, 80];

/** Center of the golden band; difficulty narrows the band around this point. */
export const BAND_CENTER = (GOLDEN_BAND[0] + GOLDEN_BAND[1]) / 2; // 67.5

/** Base full band width (max - min) before any difficulty shrink. */
export const BASE_BAND_WIDTH = GOLDEN_BAND[1] - GOLDEN_BAND[0]; // 25

/** Narrowest the band is ever allowed to shrink to (heat units). */
export const MIN_BAND_WIDTH = 8;

/** Heat above this chars the marshmallow → burnt. Sits above the band so a
 *  small overshoot only stalls toasting; a big overshoot burns. */
export const BURN_THRESHOLD = 85;

/** Toastedness (0..100) a marshmallow must reach to count as toasted. */
export const TOAST_TARGET = 100;

/** Toastedness gained per second while heat is inside the golden band. */
export const TOAST_RATE_PER_SEC = 25;

/** Heat-in rate at full proximity (distanceNorm = 0) at base difficulty,
 *  in heat units per second. Scaled by (1 - distanceNorm) and difficulty. */
export const BASE_FLAME_INTENSITY = 60;

/** Ambient cooling, heat units lost per second regardless of distance. */
export const COOLING_PER_SEC = 18;

/** Runs end after this many marshmallows are burnt. */
export const BURN_LIMIT = 3;

/** Largest dt (seconds) the sim will integrate in one step — clamps spikes so
 *  a stalled tab can't teleport heat across the burn threshold. */
export const MAX_DT = 1 / 20;

// ── Difficulty ramp (per marshmallow completed) ─────────────────────────────

/** Flame intensity added per completed marshmallow. */
export const FLAME_STEP_PER_MARSHMALLOW = 4;
/** Flame intensity is clamped to this maximum. */
export const MAX_FLAME_INTENSITY = 150;

/** Band width removed per completed marshmallow (band gets tighter). */
export const BAND_SHRINK_PER_MARSHMALLOW = 1.5;

/** Wind amplitude (heat units of perturbation) at base difficulty. */
export const BASE_WIND_AMP = 0;
/** Wind amplitude added per completed marshmallow. */
export const WIND_STEP_PER_MARSHMALLOW = 2;
/** Wind amplitude is clamped to this maximum. */
export const MAX_WIND_AMP = 60;

// ── Scoring ─────────────────────────────────────────────────────────────────

/** Flat points awarded for any completed marshmallow. */
export const BASE_POINTS = 100;
/** Extra points scaled by quality (0..1, how centered in the band). */
export const QUALITY_BONUS = 150;
/** Reference toast time (seconds); finishing faster earns a speed bonus. */
export const SPEED_REF_SECONDS = 8;
/** Points per second saved under the speed reference. */
export const SPEED_FACTOR = 12;
/** Each consecutive non-burnt marshmallow adds this to the combo multiplier. */
export const COMBO_STEP = 0.15;
